import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Chip, Collapse, MenuItem, Select, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useCan } from "../../auth/Can";
import { AgentBoardRow, getAgentBoard, getMyDesk, updateMyDesk } from "../../services/supportService";
import { QUEUE_LABELS } from "../../constants/support";

/**
 * Phase 2 desk (§5.4): my status (On duty / Break / Off) and who else is on duty with what load. Going off duty with
 * live tickets lists them so they can be transferred first (not blocked).
 */
export const STATUS_TEXT: Record<string, string> = { on_duty: "On duty", break: "Break", off: "Off" };
const STATUS_COLOR: Record<string, "success" | "warning" | "default"> = { on_duty: "success", break: "warning", off: "default" };

const AgentDesk: React.FC = () => {
    const { can } = useCan();
    const navigate = useNavigate();
    const [status, setStatus] = useState<string>("");
    const [handOver, setHandOver] = useState<{ id: string; publicNumber: string | null }[]>([]);
    const [board, setBoard] = useState<AgentBoardRow[]>([]);
    const [showBoard, setShowBoard] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => { getMyDesk().then((r) => setStatus(r.data.status)).catch(() => undefined); }, []);
    useEffect(() => { if (showBoard) getAgentBoard().then((r) => setBoard(r.data.agents)).catch(() => setBoard([])); }, [showBoard]);

    const change = async (next: string) => {
        try {
            const r = await updateMyDesk({ status: next });
            setStatus(r.data.profile.status);
            setHandOver(r.data.handOver);
            setError(null);
        } catch (e: any) {
            setError(e?.response?.data?.message || "Could not change your status");
        }
    };

    return (
        <Box sx={{ px: 2, py: 1 }} data-testid="agent-desk">
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>My desk</Typography>
                {can("support:write") && status ? (
                    <Select size="small" value={status} onChange={(e) => change(String(e.target.value))} inputProps={{ "aria-label": "My status" }}>
                        {Object.entries(STATUS_TEXT).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                    </Select>
                ) : status ? <Chip size="small" label={STATUS_TEXT[status] ?? status} /> : null}
                <Button size="small" onClick={() => setShowBoard((s) => !s)}>{showBoard ? "Hide agents" : "Show agents"}</Button>
            </Stack>
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
            {handOver.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                    You still have {handOver.length} open ticket(s) — transfer them before you leave:{" "}
                    {handOver.map((t) => (
                        <Button key={t.id} size="small" onClick={() => navigate(`/support-ticket/${t.id}`)}>{t.publicNumber || `#${t.id}`}</Button>
                    ))}
                </Alert>
            )}
            <Collapse in={showBoard}>
                <Table size="small" sx={{ mt: 1 }}>
                    <TableHead><TableRow><TableCell>Agent</TableCell><TableCell>Status</TableCell><TableCell>Load</TableCell><TableCell>Queues</TableCell><TableCell>Languages</TableCell></TableRow></TableHead>
                    <TableBody>
                        {board.length === 0 && <TableRow><TableCell colSpan={5}>No support agents.</TableCell></TableRow>}
                        {board.map((a) => (
                            <TableRow key={a.id}>
                                <TableCell>{a.name}</TableCell>
                                <TableCell><Chip size="small" color={STATUS_COLOR[a.status] ?? "default"} label={STATUS_TEXT[a.status] ?? a.status} /></TableCell>
                                <TableCell sx={{ color: a.load >= a.capacity ? "error.main" : undefined }}>{a.load} / {a.capacity}</TableCell>
                                <TableCell>{a.queues.map((q) => QUEUE_LABELS[q] ?? q).join(", ")}</TableCell>
                                <TableCell>{a.languages.join(", ")}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Collapse>
        </Box>
    );
};

export default AgentDesk;
